import { ReactElement, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { HiOutlinePencil, HiOutlineCheckCircle, HiOutlineXMark } from 'react-icons/hi2';
import { toast } from 'react-hot-toast';
import { updateRun } from '../../../api/runs';

interface RunNotesProps {
  run: any;
}

const htmlToTextarea = (html: string) => html.replace(/<br\s*\/?>/gi, '\n');

export default function RunNotes({ run }: RunNotesProps): ReactElement {
  const location = useLocation();
  const [notesContent, setNotesContent] = useState(htmlToTextarea(run?.notes || ''));
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [isSavingNotes, setIsSavingNotes] = useState(false);

  useEffect(() => {
    if (!isEditingNotes) {
      setNotesContent(htmlToTextarea(run?.notes || ''));
    }
  }, [run?.notes, isEditingNotes]);

  useEffect(() => {
    if (location.state?.activateNotesEdit) {
      setIsEditingNotes(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleSaveNotes = async () => {
    if (!run?.id) {
      toast.error('Cannot save notes for this run');
      return;
    }
    setIsSavingNotes(true);
    try {
      const formattedContent = notesContent.replace(/\r?\n/g, '<br>');
      await updateRun(run.id, { id: run.id, notes: formattedContent });
      toast.success('Notes saved successfully');
      setIsEditingNotes(false);
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.error('Error saving notes');
    } finally {
      setIsSavingNotes(false);
    }
  };

  const handleCancelNotes = () => {
    setNotesContent(htmlToTextarea(run?.notes || ''));
    setIsEditingNotes(false);
  };

  return (
    <div
      className={`cr-group cr-relative cr-w-full cr-rounded-lg cr-bg-white cr-p-4 cr-shadow-md ${
        !isEditingNotes ? 'cr-cursor-pointer' : ''
      }`}
      onClick={() => !isEditingNotes && setIsEditingNotes(true)}
    >
      {!isEditingNotes && (
        <div
          className="cr-absolute cr-right-2 cr-top-2 cr-opacity-0 cr-transition-opacity cr-duration-200 group-hover:cr-opacity-100"
        >
          <HiOutlinePencil className="cr-text-xl cr-text-blue-900" />
        </div>
      )}
      <label className="font-montserrat cr-mb-2 cr-block cr-text-lg cr-font-bold cr-uppercase cr-text-blue-900">
        Notes
      </label>
      {isEditingNotes ? (
        <div className="cr-flex cr-flex-col cr-gap-2" onClick={(e) => e.stopPropagation()}>
          <textarea
            className="cr-min-h-[150px] cr-w-full cr-rounded cr-border cr-border-gray-300 cr-p-2 cr-font-main cr-text-sm cr-text-blue-900 focus:cr-border-blue-500 focus:cr-outline-none"
            value={notesContent}
            onChange={(e) => setNotesContent(e.target.value)}
            placeholder="Enter notes content (HTML supported)..."
          />
          <div className="cr-flex cr-justify-end cr-gap-2">
            <button
              onClick={handleCancelNotes}
              className="cr-flex cr-items-center cr-gap-1 cr-rounded cr-bg-gray-200 cr-px-3 cr-py-1.5 cr-text-sm cr-text-gray-700 hover:cr-bg-gray-300"
            >
              <HiOutlineXMark className="cr-h-4 cr-w-4" />
              Cancel
            </button>
            <button
              onClick={handleSaveNotes}
              disabled={isSavingNotes}
              className="cr-flex cr-items-center cr-gap-1 cr-rounded cr-bg-blue-900 cr-px-3 cr-py-1.5 cr-text-sm cr-text-white hover:cr-bg-blue-850 disabled:cr-opacity-50"
            >
              <HiOutlineCheckCircle className="cr-h-4 cr-w-4" />
              {isSavingNotes ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      ) : (
        <div
          className="cr-font-main cr-text-sm cr-text-blue-900"
          dangerouslySetInnerHTML={{ __html: run?.notes || '' }}
        />
      )}
    </div>
  );
}
